import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router"
import { LogoFull } from "@/components/LogoFull"
import { usePageTitle } from "@/contexts/PageTitleContext.tsx"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button.tsx"
import {
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
  Field,
  FieldError,
} from "@/components/ui/field.tsx"
import { Input } from "@/components/ui/input.tsx"
import { useMutation } from "@tanstack/react-query"
import { authApi } from "@/api/auth.api.ts"
import {
  REFRESH_TOKEN_EXPIRES_AT_NAME,
  REFRESH_TOKEN_NAME,
  TOKEN_EXPIRES_AT_NAME,
  TOKEN_NAME,
} from "@/api/client.ts"
import { LoaderCircle } from "lucide-react"
import { LanguageSelector } from "@/components/LanguageSelector.tsx"
import { useTranslation } from "react-i18next"
import { cn, getStoredLocale } from "@/lib/utils.ts"

export const Route = createFileRoute("/_unauthorized/login")({
  ssr: false,
  component: Login,
})

function Login() {
  const { setTitle } = usePageTitle()
  const navigate = useNavigate()
  const { redirect } = useSearch({ from: "/_unauthorized/login" })
  const { t } = useTranslation()
  const locale = getStoredLocale()

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")

  const authenticate = useMutation({
    mutationFn: authApi.authenticate,
    onSuccess: (data) => {
      const now = Date.now()
      localStorage.setItem(TOKEN_NAME, data.result?.accessToken)
      localStorage.setItem(REFRESH_TOKEN_NAME, data.result?.refreshToken)
      localStorage.setItem(
        TOKEN_EXPIRES_AT_NAME,
        (now + data.result?.expireInSeconds * 1000).toString()
      )
      localStorage.setItem(
        REFRESH_TOKEN_EXPIRES_AT_NAME,
        (now + data.result?.refreshTokenExpireInSeconds * 1000).toString()
      )
    },
  })

  const firebaseLogin = useMutation({
    mutationFn: authApi.loginWithFirebaseEmailPassword,
    onError: (error) => {
      setIsError(true)
    },
  })

  const googleLogin = useMutation({
    mutationFn: authApi.loginWithGoogle,
    onError: (error) => {
      setIsError(true)
    },
  })

  const loginWithGoogle = async () => {
    googleLogin.mutate(
      {},
      {
        onSuccess: async () => {
          await navigate({ to: redirect ? `${redirect}` : "/pitches" })
        },
        onError: (error) => {
          setIsError(true)
        },
      }
    )
  }

  const getAuthenticationSource = useMutation({
    mutationFn: authApi.getAuthenticationSource,
    onSuccess: (data) => {
      if (data.result === "Firebase") {
        firebaseLogin.mutate(
          { email, password },
          {
            onSuccess: async () => {
              await navigate({ to: redirect ? `${redirect}` : "/pitches" })
            },
            onError: (error) => {
              setIsError(true)
            },
          }
        )
      }
      if (data.result === "Local") {
        authenticate.mutate(
          { userNameOrEmailAddress: email, password },
          {
            onSuccess: async () => {
              await navigate({ to: redirect ? `${redirect}` : "/pitches" })
            },
            onError: (error) => {
              setIsError(true)
            },
          }
        )
        // localStorage.setItem(TOKEN_NAME, data.result.accessToken)
      }
    },
    onError: (error) => {
      setIsError(true)
    },
    onSettled: () => {
      setIsSubmitting(false)
    },
  })

  const onChangeEmail = (e) => {
    setEmail(e.target.value)
  }
  const onChangePassword = (e) => {
    setPassword(e.target.value)
  }

  const onClickLogin = (e) => {
    e.preventDefault()
    setIsError(false)
    setIsSubmitting(true)
    getAuthenticationSource.mutate(email)
  }

  useEffect(() => {
    setTitle(t("login.login"))
  }, [setTitle])

  return (
    <div className="flex h-full w-full max-w-md flex-col items-center p-4 text-center">
      <LogoFull />
      <h2
        className={cn(
          "mb-4 text-center font-heading text-xl",
          locale === "ar" && "font-arabic!"
        )}
      >
        {t("login.title")}
      </h2>
      <div className="flex flex-col gap-6">
        <FieldDescription className="text-center">
          {t("login.noAccount")}{" "}
          <Link to={"/signup"}> {t("login.signup")}</Link>
        </FieldDescription>
        <form>
          <FieldGroup>
            {isError && (
              <FieldError className="rounded-sm bg-background p-2">
                {t("login.error")}
              </FieldError>
            )}
            <Field>
              <FieldLabel htmlFor="email">{t("login.email")}</FieldLabel>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                required
                className="h-10"
                value={email}
                onChange={onChangeEmail}
              />
            </Field>
            <Field>
              <div className="flex items-center">
                <FieldLabel htmlFor="password">
                  {t("login.password")}
                </FieldLabel>
                <Link
                  to={"/account/forget-password"}
                  className="ms-auto inline-block text-sm underline-offset-4 hover:underline"
                >
                  {t("login.forget")}
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder={t("login.password")}
                required
                className="h-10"
                value={password}
                onChange={onChangePassword}
              />
            </Field>
            <Field>
              <Button
                type="submit"
                disabled={isSubmitting}
                onClick={onClickLogin}
                size="lg"
              >
                {isSubmitting ? (
                  <LoaderCircle className="animate-spin" />
                ) : (
                  t("login.login")
                )}
              </Button>
              <FieldSeparator className="mt-4 mb-4 uppercase *:data-[slot=field-separator-content]:bg-sidebar">
                {t("login.continue")}
              </FieldSeparator>
              <Button
                variant="outline"
                size="lg"
                type="button"
                onClick={loginWithGoogle}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path
                    d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                    fill="currentColor"
                  />
                </svg>
                {t("login.google")}
              </Button>
            </Field>
          </FieldGroup>
        </form>
        <FieldDescription className="px-6 text-center">
          {t("login.agreement")} <a href="#">{t("login.terms")}</a>{" "}
          {t("login.and")} <a href="#">{t("login.privacy")}</a>.
        </FieldDescription>
      </div>
      <div>
        <LanguageSelector />
      </div>
    </div>
  )
}
